/**
 * Redux slice du « Face à face » (chantier C23 « Confrontation », SP2, spec § 7.1) :
 * le côté de l'auteur d'une note que je peux lire, tel que le serveur le rend.
 *
 * Calqué sur `apercuConceptSlice` : l'état est indexé par **note et concept**
 * (`byCle[noteId:entreeId]`) — le droit tient à la note —, une garde `requestId`
 * écarte la réponse périmée d'un même couple, un 404 est un état (« indisponible »),
 * aucune notification globale. Une différence : chaque lecture repart de rien
 * (`pending` efface la réponse précédente). Le droit a pu être retiré, un passage a pu
 * changer : on ne montre jamais un face à face d'avant.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { noteService } from '../../services/noteAPI';
import { organisationAPI } from '../../services/organisationAPI';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import type { FaceAFaceDto } from '../../types/api';
import { porteDeSource, type SourceConcept } from '../../types/sourceConcept';

export type FaceAFaceStatus = 'idle' | 'loading' | 'ready' | 'indisponible' | 'error';

export interface FaceAFaceEntry {
    status: FaceAFaceStatus;
    data?: FaceAFaceDto;
    /** Dernier chargement lancé pour ce couple : garde anti-course. */
    requestId: string | null;
}

export interface FaceAFaceState {
    byCle: Record<string, FaceAFaceEntry>;
}

export const initialFaceAFaceState: FaceAFaceState = { byCle: {} };

export const cleFaceAFace = (noteId: string, entreeId: string): string => `${noteId}:${entreeId}`;

/** L'argument de la lecture : la source (note OU organisation, jamais les deux ni aucune), le concept, le jeton. */
export type FaceAFaceArg = SourceConcept & { entreeId: string; instance: any };

/** Entrée de repli des sélecteurs : référence stable, sinon re-rendus en boucle. */
const EMPTY_ENTRY: FaceAFaceEntry = Object.freeze({ status: 'idle', requestId: null }) as FaceAFaceEntry;

async function tokenOf(instance: any): Promise<string> {
    const accessToken = await getTokenRedirect(tokenRequest, instance);
    if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
    return accessToken;
}

export const fetchFaceAFace = createAsyncThunk<
    FaceAFaceDto,
    FaceAFaceArg,
    { rejectValue: 'indisponible' | 'error' }
>('faceAFace/fetch', async (arg, { rejectWithValue }) => {
    try {
        const token = await tokenOf(arg.instance);
        if (arg.organisationId !== undefined) {
            const data = await organisationAPI.faceAFaceConcept(arg.organisationId, arg.entreeId, token);
            return data ?? rejectWithValue('indisponible');
        }
        return await noteService.getFaceAFace(arg.noteId, arg.entreeId, token);
    } catch (error: any) {
        return rejectWithValue(error?.response?.status === 404 ? 'indisponible' : 'error');
    }
});

const faceAFaceSlice = createSlice({
    name: 'faceAFace',
    initialState: initialFaceAFaceState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchFaceAFace.pending, (state, action) => {
                state.byCle[cleFaceAFace(porteDeSource(action.meta.arg), action.meta.arg.entreeId)] = { status: 'loading', requestId: action.meta.requestId };
            })
            .addCase(fetchFaceAFace.fulfilled, (state, action) => {
                const cle = cleFaceAFace(porteDeSource(action.meta.arg), action.meta.arg.entreeId);
                const entry = state.byCle[cle];
                if (!entry || entry.requestId !== action.meta.requestId) return;
                state.byCle[cle] = { status: 'ready', data: action.payload, requestId: action.meta.requestId };
            })
            .addCase(fetchFaceAFace.rejected, (state, action) => {
                const cle = cleFaceAFace(porteDeSource(action.meta.arg), action.meta.arg.entreeId);
                const entry = state.byCle[cle];
                if (!entry || entry.requestId !== action.meta.requestId) return;
                state.byCle[cle] = { status: action.payload === 'indisponible' ? 'indisponible' : 'error', requestId: action.meta.requestId };
            });
    },
});

export default faceAFaceSlice.reducer;

// --- Sélecteurs (tolérants : un store partiel rend l'entrée vide) ---
export const selectFaceAFace = (noteId: string, entreeId: string) => (state: any): FaceAFaceEntry =>
    state?.faceAFace?.byCle?.[cleFaceAFace(noteId, entreeId)] ?? EMPTY_ENTRY;
